import { useOptionalUser } from "@/lib/Auth/useUser";
import { ReactNode, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/catalyst/button";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { SignalSlashIcon } from "@heroicons/react/24/outline";
import { useBuildSha } from "@/app/Meta/utils/useBuildSha";
import { NetworkError } from "@/lib/GraphQLCodegen/fetcher";
import { reloadOnStaleChunk } from "@/lib/Utils/reloadOnStaleChunk";

type ErrorBoundaryProps = {
  children: ReactNode;
};

function ErrorFallback({ error, resetError, componentStack }: any) {
  const [showDetails, setShowDetails] = useState(false);
  const [isReloading] = useState(() => reloadOnStaleChunk(error));
  const { data, isLoading } = useBuildSha();
  const [href, setHref] = useState(window.location.href);
  const prevHrefRef = useRef(href);

  useEffect(() => {
    const onChange = () => setHref(window.location.href);
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);

  useEffect(() => {
    if (href !== prevHrefRef.current) {
      //Not sure why this needs a timeout...

      setTimeout(() => {
        resetError();
      }, 100);
    }
  }, [href, resetError]);

  const isOutdated =
    !import.meta.env.DEV &&
    !isLoading &&
    data?.sha !== import.meta.env.VITE_BUILD_SHA;

  const isNetworkError = error instanceof NetworkError;

  const formatErrorMessage = (error: any) => {
    try {
      const errorMessage = error?.message || "Unknown error";

      // Check if it's potentially a GraphQL error with JSON content
      if (
        typeof errorMessage === "string" &&
        (errorMessage.includes("Internal server error") ||
          errorMessage.includes("GraphQL"))
      ) {
        // Try to extract and parse JSON from the error message
        const jsonMatch = errorMessage.match(/({[\s\S]*})/);
        if (jsonMatch && jsonMatch[0]) {
          try {
            const parsedError = JSON.parse(jsonMatch[0]) as any;

            if (parsedError.response && parsedError.response.errors) {
              return (
                <div className="max-h-96 space-y-3 overflow-auto">
                  {parsedError.response.errors.map((err: any, i: number) => (
                    <div
                      key={i}
                      className="border-l-4 border-red-800 bg-red-700/20 py-1 pl-3"
                    >
                      <div className="font-medium">{err.message}</div>
                      {err.path && (
                        <div className="text-sm">
                          Path: {err.path.join(".")}
                        </div>
                      )}
                      {err.extensions?.debugMessage && (
                        <div className="mt-1 text-sm">
                          {err.extensions.debugMessage}
                        </div>
                      )}
                      {err.locations?.map((loc: any, j: number) => (
                        <div key={j} className="text-sm">
                          Location: Line {loc.line}, Column {loc.column}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            }
          } catch (e: any) {
            console.error(e);
            return;
          }
        }
      }

      // Default formatting with proper line breaks
      return (
        <div className="whitespace-pre-wrap break-words">{errorMessage}</div>
      );
    } catch (e: any) {
      console.error(e);
      return "Error formatting error message";
    }
  };

  const formatComponentStack = (stack: string | undefined) => {
    if (!stack) return null;

    return (
      <div className="mt-4 max-h-96 overflow-auto rounded-lg bg-gray-700 p-4 text-sm text-white">
        <pre className="whitespace-pre-wrap break-words">{stack}</pre>
      </div>
    );
  };

  if (isReloading) {
    return <></>;
  }

  if (isNetworkError && !isOutdated) {
    return (
      <div className="flex justify-center px-4 py-16">
        <div className="w-full max-w-sm text-center">
          <SignalSlashIcon className="mx-auto size-10 text-gray-400" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">
            Network Connection Issue
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Unable to connect to the server. Please check your internet
            connection.
          </p>

          <div className="mt-6 flex justify-center">
            <Button onClick={resetError} LeadingIcon={ArrowPathIcon}>
              Try Again
            </Button>
          </div>

          <ul className="mt-8 space-y-1 border-t border-gray-200 pt-6 text-left text-xs text-gray-500">
            <li>Check your internet connection</li>
            <li>Try refreshing the page</li>
            <li>Clear your browser cache</li>
          </ul>
          <p className="mt-3 break-words text-left font-mono text-xs text-gray-400">
            {error.message}
          </p>
          <div className="mt-4 text-left">
            <UserInfo />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-6">
      <div className="container space-y-6 rounded-md bg-gray-100 p-6">
        <UserInfo />

        {isOutdated ? (
          <>
            <div className="text-center">
              <div className="mb-4 text-6xl">🔄</div>
              <h1 className="mb-4 text-3xl font-bold text-gray-800">
                {"Let's get you updated!"}
              </h1>
              <p className="mb-6 text-lg text-gray-600">
                A new version is available that should fix this issue.
              </p>
            </div>
            <div className="flex justify-center">
              <Button
                onClick={() => window.location.reload()}
                LeadingIcon={ArrowPathIcon}
              >
                Reload Now
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-xl font-bold">Something went wrong:</h1>
            <div className="w-full overflow-auto rounded-xl bg-red-600 p-4 text-white shadow-md">
              {formatErrorMessage(error)}
            </div>

            {showDetails && formatComponentStack(componentStack)}

            <div className="flex space-x-3">
              <Button onClick={resetError}>Try again</Button>

              <Button outline onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? "Hide" : "Show"} details
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UserInfo() {
  const user = useOptionalUser();
  if (!user) return null;
  return (
    <div className="text-sm text-gray-700">
      Signed in as <span className="font-medium">{user.email}</span>
    </div>
  );
}

export function GenericErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
